import { Trans } from "@lingui/react/macro";

import { useAnalysisUsage } from "@/features/analysis";
import { useCurrentUser } from "@/features/auth";
import { useStatsSummary } from "@/features/matches";
import { Button } from "@/shared/ui/button";
import { Skeleton } from "@/shared/ui/skeleton";

import { DEFAULT_ACTIVITY_PERIOD } from "../../model/ACTIVITY_PERIODS";
import type { ProfileSearch } from "../../model/profileSearchSchema";
import { AccountSection } from "./AccountSection";
import { Activity } from "./Activity";
import { ProfileHeader } from "./ProfileHeader";
import { RankPath } from "./RankPath";

// Экран «Профиль» по макету design/prototype/ProfileRelease.dc.html: шапка с обложкой героя,
// рангом и итогами, путь по рангам, активность по месяцам и аккаунт. Данные — текущий
// пользователь (`GET /api/me`), сводка игрока (`GET /api/stats/summary`) и счётчик разборов
// (`GET /api/analyses/usage`). Период «Активности» хранит маршрут в search params.
type ProfilePageProps = {
  search: ProfileSearch;
  onSearchChange: (search: ProfileSearch) => void;
};

export const ProfilePage = ({ search, onSearchChange }: ProfilePageProps) => {
  const { data: user } = useCurrentUser();
  const stats = useStatsSummary();
  const usage = useAnalysisUsage();
  const summary = stats.data?.summary ?? null;
  const pending = stats.isPending || stats.data?.status === "pending";

  const rankAndActivity = () => {
    if (pending) {
      return <Skeleton data-state="loading" className="h-80 rounded-xl" />;
    }
    if (stats.isError) {
      return (
        <div className="flex flex-wrap items-center gap-3 rounded-xl border border-border bg-surface p-5">
          <p className="text-foreground-soft">
            <Trans>Could not load your stats.</Trans>
          </p>
          <Button variant="outline" onClick={() => void stats.refetch()}>
            <Trans>Try again</Trans>
          </Button>
        </div>
      );
    }
    return <RankPath history={summary?.rank_history ?? []} />;
  };

  return (
    <main
      data-page="profile"
      className="mx-auto flex w-full max-w-[1200px] flex-col gap-5 px-6 pt-6 pb-16"
    >
      {user !== undefined && (
        <ProfileHeader
          user={user}
          summary={summary}
          pending={pending}
          analyzedMatches={usage.data?.analyzed_matches}
        />
      )}
      {rankAndActivity()}
      <div className="grid grid-cols-[repeat(auto-fit,minmax(min(380px,100%),1fr))] gap-5">
        {summary !== null && (
          <Activity
            months={summary.monthly_matches}
            days={summary.daily_matches}
            period={search.activity ?? DEFAULT_ACTIVITY_PERIOD}
            onPeriodChange={(activity) => {
              onSearchChange({ ...search, activity });
            }}
          />
        )}
        {user !== undefined && <AccountSection steamId={user.steam_id} />}
      </div>
    </main>
  );
};
